/**
 * Cross-tab session signalling.
 *
 * Five tabs of a mail client share one cookie jar, so they share one session:
 * if one tab signs out, the other four are holding a cache of someone else's
 * mail and a UI that claims to be signed in. If one tab re-authenticates, the
 * other four should stop asking for a password.
 *
 * ## What may cross the channel
 *
 * **Signals only.** A message is a verb and a timestamp. No token, no cookie
 * value, no password, not even the user's address: the session cookies are
 * `HttpOnly` precisely so that no script holds session material, and a
 * broadcast that carried any would hand it back to every script on the origin —
 * and, on the `localStorage` fallback path, write it to disk. Each tab reacts
 * to a signal by re-deriving its own state from `GET /auth/session`, which is
 * the only authority anyway.
 *
 * ## Transport
 *
 * `BroadcastChannel` where available. The fallback is a `localStorage` write
 * whose `storage` event other tabs observe — used as a doorbell and nothing
 * else; the value is a verb plus a nonce (so two identical signals still fire),
 * and it is removed immediately.
 *
 * Two signals carry a payload. `revoked` carries *why* — one of a closed set
 * of reason codes, so every tab's sign-in screen can say the same true thing.
 * `refreshed` carries the new expiry (ADR 0082): when an active tab's requests
 * slide the session, or its user presses *Stay signed in*, every other tab's
 * idle banner has to move too, or a person working in one tab is warned in
 * another. A reason code and a timestamp are not session material.
 */

import type { RevocationReason } from '@/api/sessionSignals';

export type SessionBroadcastType =
  | 'login'
  | 'logout'
  | 'expired'
  | 'reauthenticated'
  | 'revoked'
  | 'refreshed';

export interface SessionBroadcast {
  readonly type: SessionBroadcastType;
  /** Epoch millis — lets a listener ignore a stale replay. Not identity. */
  readonly at: number;
  /** Only on `revoked`: why the server ended the session. */
  readonly reason?: RevocationReason;
  /** Only on `refreshed`: the session's new expiry, ISO 8601, server clock. */
  readonly expiresAt?: string;
}

export const SESSION_CHANNEL_NAME = 'ols-mail.session';
/** `localStorage` fallback key. Holds a verb and a nonce, never a credential. */
export const SESSION_SIGNAL_KEY = 'ols-mail.session-signal';

export interface SessionChannel {
  post(type: SessionBroadcastType, reason?: RevocationReason): void;
  /** `refreshed`, with the expiry the other tabs should now count down to. */
  postRefreshed(expiresAt: string): void;
  subscribe(listener: (message: SessionBroadcast) => void): () => void;
  close(): void;
}

function isBroadcast(value: unknown): value is SessionBroadcast {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { type?: unknown; at?: unknown; reason?: unknown; expiresAt?: unknown };
  if (candidate.type === 'refreshed') {
    return (
      typeof candidate.at === 'number' &&
      typeof candidate.expiresAt === 'string' &&
      Number.isFinite(Date.parse(candidate.expiresAt))
    );
  }
  return (
    typeof candidate.at === 'number' &&
    candidate.expiresAt === undefined &&
    (candidate.type === 'login' ||
      candidate.type === 'logout' ||
      candidate.type === 'expired' ||
      candidate.type === 'reauthenticated' ||
      candidate.type === 'revoked') &&
    (candidate.reason === undefined ||
      candidate.reason === 'password_changed' ||
      candidate.reason === 'signed_out_elsewhere' ||
      candidate.reason === 'unknown')
  );
}

/**
 * The channel is **reopened on use after `close()`**, and `post` never throws.
 *
 * `SessionProvider` creates one in `useState` and closes it in an effect's
 * cleanup. Under React StrictMode (every dev build) that cleanup runs once on
 * the simulated unmount and the component is then remounted with the *same*
 * channel — so a channel that stayed closed made every later `post` throw
 * `InvalidStateError: Channel is closed` and silenced every other tab. The
 * throw surfaced as a revoked session that never reached the sign-in screen:
 * `post('revoked')` threw before logout and navigation could run.
 */
export function createSessionChannel(name: string = SESSION_CHANNEL_NAME): SessionChannel {
  const listeners = new Set<(message: SessionBroadcast) => void>();
  const deliver = (message: SessionBroadcast): void => {
    for (const listener of [...listeners]) listener(message);
  };

  const hasBroadcastChannel = typeof BroadcastChannel === 'function';
  let channel: BroadcastChannel | undefined;
  let storageListening = false;

  const onStorage = (event: StorageEvent): void => {
    if (event.key !== SESSION_SIGNAL_KEY || event.newValue === null) return;
    try {
      const parsed: unknown = JSON.parse(event.newValue);
      if (isBroadcast(parsed)) deliver(parsed);
    } catch {
      /* Another tab wrote something we do not understand — ignore it. */
    }
  };

  /** Opens (or reopens) both transports. Idempotent. */
  const open = (): void => {
    if (hasBroadcastChannel && channel === undefined) {
      channel = new BroadcastChannel(name);
      channel.onmessage = (event: MessageEvent<unknown>) => {
        if (isBroadcast(event.data)) deliver(event.data);
      };
    }
    // Registered even when BroadcastChannel exists: a tab in a browser that
    // only has one of the two still has to be heard.
    if (!storageListening && typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      storageListening = true;
    }
  };
  open();

  const send = (message: SessionBroadcast): void => {
    open();
    try {
      channel?.postMessage(message);
    } catch {
      // Telling the other tabs is best effort; it must never stop this tab
      // from acting on the news itself.
    }
    if (channel === undefined) {
      try {
        // The nonce keeps two identical signals from collapsing into one
        // `storage` event; the immediate removal keeps nothing on disk.
        localStorage.setItem(
          SESSION_SIGNAL_KEY,
          JSON.stringify({ ...message, nonce: Math.random().toString(36).slice(2) }),
        );
        localStorage.removeItem(SESSION_SIGNAL_KEY);
      } catch {
        /* Storage blocked: this tab simply cannot signal the others. */
      }
    }
  };

  return {
    post(type, reason) {
      send(reason === undefined ? { type, at: Date.now() } : { type, at: Date.now(), reason });
    },
    postRefreshed(expiresAt) {
      send({ type: 'refreshed', at: Date.now(), expiresAt });
    },
    subscribe(listener) {
      open();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    close() {
      listeners.clear();
      channel?.close();
      channel = undefined;
      if (storageListening && typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
        storageListening = false;
      }
    },
  };
}
